import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Circle, Loader2, PackageSearch } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../lib/api";
import { useSeo } from "../lib/seo";

const STEP_ORDER = ["confirmed", "packed", "shipped", "out_for_delivery", "delivered"];
const STEP_LABELS = {
  confirmed: "Order confirmed",
  packed: "Packed with love",
  shipped: "Shipped",
  out_for_delivery: "Out for delivery",
  delivered: "Delivered",
};

export default function TrackOrder() {
  useSeo({
    title: "Track Your Order",
    description:
      "Track your Sparkwear order — follow it from confirmed to packed, shipped, out for delivery and delivered.",
  });
  const [params] = useSearchParams();
  const [orderId, setOrderId] = useState(params.get("order") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!orderId.trim() || !email.trim()) {
      toast.error("Enter your order number and the email you used.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/orders/track`, {
        params: { order_id: orderId.trim(), email: email.trim() },
      });
      setResult(data);
    } catch (err) {
      setResult(null);
      toast.error(err?.response?.data?.detail || "No order found for those details.");
    } finally {
      setLoading(false);
    }
  };

  const currentStep = result ? result.delivery_status : null;
  const reachedIndex = currentStep ? STEP_ORDER.indexOf(currentStep) : -1;

  useEffect(() => {
    if (params.get("order") && params.get("email") && !result && !loading) {
      submit({ preventDefault: () => {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="mx-auto min-h-[70vh] w-full max-w-3xl px-5 py-14 sm:px-8">
      <h1 className="font-display text-4xl font-semibold text-ink sm:text-5xl">Track your order</h1>
      <p className="mt-3 text-muted">
        Pop in your order number (it starts with SPK-) and the email you used at checkout.
      </p>

      <form
        onSubmit={submit}
        data-testid="track-order-form"
        className="mt-8 rounded-[28px] border border-lav-100 bg-white p-6 sm:p-8"
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-muted">Order number</span>
            <input
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder="SPK-1F8D18F6"
              data-testid="track-order-input"
              className="mt-2 w-full rounded-2xl border border-lav-100 bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors duration-200 focus:border-blush-500"
            />
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-muted">Email</span>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="track-email-input"
              className="mt-2 w-full rounded-2xl border border-lav-100 bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors duration-200 focus:border-blush-500"
            />
          </label>
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="track-submit-button"
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink py-4 font-display text-base font-semibold text-white transition-transform duration-300 hover:scale-[1.02] disabled:opacity-70"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {loading ? "Looking..." : "Find my order"}
        </button>
      </form>

      {!result && !loading && (
        <div className="mt-8 rounded-[28px] border border-dashed border-lav-300 bg-white/60 p-10 text-center" data-testid="track-empty-state">
          <PackageSearch className="mx-auto h-10 w-10 text-lav-500" />
          <p className="mt-4 font-display text-xl font-semibold text-ink">Your order journey will appear here</p>
          <p className="mt-1 text-sm text-muted">
            Every Sparkwear parcel reports in: confirmed, packed, shipped, out for delivery, delivered.
          </p>
        </div>
      )}

      {result && (
        <motion.section
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          data-testid="track-result"
          className="mt-8 rounded-[28px] border border-lav-100 bg-white p-6 sm:p-8"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-display text-2xl font-semibold text-ink">{result.order_id}</p>
              <p className="text-sm text-muted">
                Placed {new Date(result.placed_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </p>
            </div>
            <span className="rounded-full bg-mint-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] text-mint-600" data-testid="track-current-status">
              {result.delivery_status_label}
            </span>
          </div>

          <ol className="mt-8 space-y-5">
            {STEP_ORDER.map((s, i) => {
              const reached = i <= reachedIndex;
              return (
                <li key={s} className="flex items-center gap-4" data-testid={`track-step-${s}`}>
                  {reached ? (
                    <CheckCircle2 className="h-6 w-6 shrink-0 text-mint-600" />
                  ) : (
                    <Circle className="h-6 w-6 shrink-0 text-lav-300" />
                  )}
                  <div>
                    <p className={`font-semibold ${reached ? "text-ink" : "text-lav-500"}`}>{STEP_LABELS[s]}</p>
                    {result.steps
                      .filter((st) => st.status === s)
                      .slice(-1)
                      .map((st, idx) => (
                        <p key={idx} className="text-xs text-muted">
                          {new Date(st.at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </p>
                      ))}
                  </div>
                </li>
              );
            })}
          </ol>

          {result.tracking_number && (
            <p className="mt-6 rounded-2xl bg-cream px-5 py-4 text-sm text-ink" data-testid="track-tracking-number">
              Postal tracking number: <strong>{result.tracking_number}</strong>
            </p>
          )}

          <div className="mt-6 border-t border-lav-50 pt-5 text-sm">
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-muted">What's coming</p>
            <ul className="mt-2 space-y-1 text-ink/75">
              {result.items.map((it, i) => (
                <li key={i}>
                  {it.qty} × {it.name} <span className="text-muted">(size {it.size}{it.colour ? ` · ${it.colour}` : ""})</span>
                </li>
              ))}
            </ul>
            <p className="mt-3 font-display text-lg font-semibold text-ink">Total £{result.total?.toFixed(2)}</p>
          </div>
        </motion.section>
      )}
    </main>
  );
}
